import { UUID } from "@bitory-gmbh/nidavellir";
import { NewSampleRequest } from "@/services/backend/samples/types/sample";
import { backendApi } from "@/services/backend/api";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { DustDensitySample } from "@/services/backend/samples/types/dust-density-sample";
import { Filter } from "@/shared/lib/filter/filter";
import { EntityId } from "@/shared/types/entity-id";
import { updateCache } from "@/services/backend/_lib/update-cache";

export const samplesDustDensityApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["SamplesDustDensitySample"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      /**
       * Creates a sample with the defaults of the company's base data.
       * Fails with 404 if the company has no base data default for the
       * kind. Use the PUT endpoint to update a sample.
       */
      samplesDustDensitySampleCreate: build.mutation<
        DustDensitySample,
        CreateRequest
      >({
        query: (request) => ({
          url: `/samples/dust-density`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["SamplesDustDensitySample", "SamplesSampleViewModel"],
      }),
      samplesDustDensitySamplePut: build.mutation<
        DustDensitySample,
        PutRequest
      >({
        query: (request) => ({
          url: `/samples/dust-density`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "SamplesDustDensitySample", id: request.id },
          "SamplesDustDensitySample",
          "SamplesSampleViewModel",
          // The backend derives the results of the report assessments of
          // the sample from its values, see SampleJudgementChanged.
          "HtzReport",
          "HtzSummary",
        ],
      }),
      samplesDustDensitySampleUpdateLab: build.mutation<
        DustDensitySample,
        UpdateLabRequest
      >({
        query: (request) => ({
          url: `/samples/dust-density/lab`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "SamplesDustDensitySample", id: request.id },
          "SamplesDustDensitySample",
          "SamplesSampleViewModel",
        ],
      }),
      samplesDustDensitySampleShow: build.query<DustDensitySample, ShowRequest>(
        {
          query: (request) => ({
            url: `/samples/dust-density`,
            method: "GET",
            params: request,
          }),
          providesTags: providesEntity("SamplesDustDensitySample"),
        },
      ),
      samplesDustDensitySampleList: build.query<List, ListRequest>({
        query: (request) => ({
          url: `/samples/dust-density/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) =>
          providesList(result?.samples, "SamplesDustDensitySample"),
      }),
      samplesDustDensitySampleDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: `/samples/dust-density`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: ["SamplesDustDensitySample", "SamplesSampleViewModel"],
      }),
    }),
  });

export const {
  useSamplesDustDensitySampleCreateMutation,
  useSamplesDustDensitySamplePutMutation,
  useSamplesDustDensitySampleUpdateLabMutation,
  useSamplesDustDensitySampleShowQuery,
  useSamplesDustDensitySampleListQuery,
  useLazySamplesDustDensitySampleListQuery,
  useSamplesDustDensitySampleDeleteMutation,
} = samplesDustDensityApi;

export type PutRequest = DustDensitySample;

export interface UpdateLabRequest {
  id: string;
  labId: EntityId;
}

export interface ShowRequest {
  id: string;
}

export type CreateRequest = NewSampleRequest;

export interface ListRequest {
  /**
   * Required by the backend: it must be active and hold at least one
   * company, otherwise the request fails its own validation.
   */
  company: Filter<UUID>;
  lab?: Filter<string>;
  document?: Filter<string>;
  limit?: number;
  offset?: number;
}

export interface List {
  samples: DustDensitySample[];
  count: number;
}

export interface DeleteRequest {
  id: string;
}

/**
 * Patches the cached dust density sample for an optimistic update. Defined here, at module
 * scope, so it keeps one identity for the whole app — see {@link updateCache}.
 */
export const updateDustDensitySampleCache = updateCache(
  samplesDustDensityApi,
  "samplesDustDensitySampleShow",
);
